import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { 
  Ticket, Plus, Trash2, Loader2, CheckCircle, XCircle,
  Calendar, Users, Percent, DollarSign
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  const [formData, setFormData] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    duration: "once",
    duration_in_months: "",
    max_redemptions: "",
    expires_at: ""
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/coupons`, { withCredentials: true });
      setCoupons(res.data.coupons || []);
    } catch (err) {
      toast.error("Error al cargar cupones");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        duration: formData.duration,
        duration_in_months: formData.duration === "repeating" ? parseInt(formData.duration_in_months) : null,
        max_redemptions: formData.max_redemptions ? parseInt(formData.max_redemptions) : null,
        expires_at: formData.expires_at || null
      };
      
      await axios.post(`${API}/superadmin/coupons`, payload, { withCredentials: true });
      toast.success("Cupón creado exitosamente");
      setShowCreateForm(false);
      setFormData({
        code: "",
        discount_type: "percentage",
        discount_value: "",
        duration: "once",
        duration_in_months: "",
        max_redemptions: "",
        expires_at: ""
      });
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear cupón");
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (code) => {
    if (!window.confirm(`¿Desactivar el cupón ${code}?`)) return;
    
    try {
      await axios.delete(`${API}/superadmin/coupons/${code}`, { withCredentials: true });
      toast.success("Cupón desactivado");
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al desactivar cupón");
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Ticket size={28} className="text-purple-400" />
              <h1 className="text-2xl font-bold text-white">Gestión de Cupones</h1>
            </div>
            <p className="text-slate-400 text-sm">
              Crea y administra códigos de descuento para Stripe
            </p>
          </div>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-purple-600 hover:bg-purple-500 text-white"
          >
            <Plus size={16} className="mr-2" />
            {showCreateForm ? "Cancelar" : "Nuevo Cupón"}
          </Button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Crear Nuevo Cupón</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-sm mb-2">Código *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="EJEMPLO2024"
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>

                <div>
                  <Label className="text-slate-400 text-sm mb-2">Tipo de Descuento *</Label>
                  <Select 
                    value={formData.discount_type}
                    onValueChange={(v) => setFormData({...formData, discount_type: v})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="percentage" className="text-white">Porcentaje (%)</SelectItem>
                      <SelectItem value="amount_off" className="text-white">Monto Fijo (USD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-400 text-sm mb-2">
                    {formData.discount_type === "percentage" ? "Porcentaje *" : "Monto (USD) *"}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    placeholder={formData.discount_type === "percentage" ? "10" : "5.00"}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>

                <div>
                  <Label className="text-slate-400 text-sm mb-2">Duración *</Label>
                  <Select 
                    value={formData.duration}
                    onValueChange={(v) => setFormData({...formData, duration: v})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="once" className="text-white">Una vez</SelectItem>
                      <SelectItem value="repeating" className="text-white">Recurrente</SelectItem>
                      <SelectItem value="forever" className="text-white">Para siempre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.duration === "repeating" && (
                  <div>
                    <Label className="text-slate-400 text-sm mb-2">Meses de Duración *</Label>
                    <Input
                      type="number"
                      value={formData.duration_in_months}
                      onChange={(e) => setFormData({...formData, duration_in_months: e.target.value})}
                      placeholder="3"
                      className="bg-slate-800 border-slate-700 text-white"
                      required={formData.duration === "repeating"}
                    />
                  </div>
                )}

                <div>
                  <Label className="text-slate-400 text-sm mb-2">Usos Máximos (Opcional)</Label>
                  <Input
                    type="number"
                    value={formData.max_redemptions}
                    onChange={(e) => setFormData({...formData, max_redemptions: e.target.value})}
                    placeholder="100"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>

                <div>
                  <Label className="text-slate-400 text-sm mb-2">Fecha de Expiración (Opcional)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  className="bg-purple-600 hover:bg-purple-500 text-white"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    "Crear Cupón"
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Coupons Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mr-3" />
              <span className="text-slate-400">Cargando cupones...</span>
            </div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No hay cupones creados</p>
              <p className="text-slate-500 text-sm mt-1">Crea tu primer cupón de descuento</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50 border-b border-slate-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase">Código</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase">Descuento</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase">Duración</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase">Usos</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase">Estado</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-slate-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {coupons.map((coupon) => (
                    <tr key={coupon.code} className="hover:bg-slate-800/30">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Ticket className="w-4 h-4 text-purple-400" />
                          <span className="font-mono text-white font-semibold">{coupon.code}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-white">
                          {coupon.discount_type === "percentage" ? (
                            <>
                              <Percent className="w-4 h-4 text-emerald-400" />
                              <span>{coupon.discount_value}%</span>
                            </>
                          ) : (
                            <>
                              <DollarSign className="w-4 h-4 text-emerald-400" />
                              <span>${coupon.discount_value}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline" className="border-slate-600 text-slate-300">
                          {coupon.duration === "once" && "Una vez"}
                          {coupon.duration === "forever" && "Para siempre"}
                          {coupon.duration === "repeating" && `${coupon.duration_in_months} meses`}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-300">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span>{coupon.times_redeemed || 0}</span>
                          {coupon.max_redemptions && (
                            <span className="text-slate-500">/ {coupon.max_redemptions}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {coupon.active ? (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Activo
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                            <XCircle className="w-3 h-3 mr-1" />
                            Inactivo
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {coupon.active && (
                          <Button
                            onClick={() => handleDeactivate(coupon.code)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats Summary */}
        {coupons.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Total Cupones</p>
                  <p className="text-white font-bold text-xl">{coupons.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Activos</p>
                  <p className="text-white font-bold text-xl">
                    {coupons.filter(c => c.active).length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Inactivos</p>
                  <p className="text-white font-bold text-xl">
                    {coupons.filter(c => !c.active).length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Total Usos</p>
                  <p className="text-white font-bold text-xl">
                    {coupons.reduce((sum, c) => sum + (c.times_redeemed || 0), 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
